import { Drawer, DrawerProps } from 'antd';
import { useTranslate } from '@tolgee/react';
import * as React from 'react';
import AvatarPng from 'views/BootGuide/assets/avatar.png';
import { css } from '@emotion/css';
import usePayPackage from '../hooks/usePayPackage';
import CurrencyCheckbox from './CurrencyCheckbox';
import PayTypeCheckbox from './PayTypeCheckbox';
import styled from '@emotion/styled';
import { CompanyAliHelp, Help } from './OrderAI/Help';
import { useIsEnterprisePkg } from '../hooks/useComboInfo';
import { Formik, useFormikContext } from 'formik';
import { useAuthorization } from 'components/Authorization';
import useSmartField from 'hooks/useSmartField';
import { LightWeightOrderResult } from './OrderAI/OrderResult';
import { WindupChildren } from 'windups';
import CheckContainer from 'components/CheckContainer';
import { useMutation } from '@tanstack/react-query';
import { getLightWeightPkgInfo } from '../../../../services/api/pay';
import { LightWeightOrderFormikState } from './OrderAI/type';
import { formatNumber } from 'utils/numberFormat';
import { map, get, find } from 'lodash-es';
import { InputNumber } from './InputNumber';
import { useEffect, useState } from 'react';
import { useMemoizedFn } from 'ahooks';

export interface OrderLightWeightAiServiceModalProps extends DrawerProps {
  lightWeightPkgInfo: Pay.LightWeightPkg;
}

export const OrderLightWeightAiServiceModal = React.memo(
  (props: OrderLightWeightAiServiceModalProps) => {
    const { visible, onClose, lightWeightPkgInfo, ...restProps } = props;
    const { t } = useTranslate();
    const { payload } = useAuthorization<Oversea.Token.Payload>();
    const initialValues: LightWeightOrderFormikState = React.useMemo(() => {
      if (!lightWeightPkgInfo) return {} as LightWeightOrderFormikState;
      const { unitOrderPackageList: _unitOrderPackageList, ...rest } = lightWeightPkgInfo;
      const unitOrderPackageList = map(_unitOrderPackageList, (item, index) => {
        return {
          ...item,
          isSelected:
            index === 0
              ? lightWeightPkgInfo.recommendOrderNum <= item.orderNumber
              : lightWeightPkgInfo.recommendOrderNum === item.orderNumber,
        };
      });
      const hasSelected = unitOrderPackageList.some((item) => item.isSelected);
      return {
        orgId: payload?.orgId!,
        currency: undefined,
        unitOrderPackageList,
        ...rest,
        isCustom: !hasSelected,
        customNum: hasSelected
          ? unitOrderPackageList[0].orderNumber
          : lightWeightPkgInfo.recommendOrderNum,
        minNum: unitOrderPackageList[0].orderNumber,
      };
    }, [lightWeightPkgInfo, payload]);

    if (!lightWeightPkgInfo) return null;
    return (
      <Drawer
        width={1216}
        footer={null}
        onClose={onClose}
        visible={visible}
        title={t('订购AI精准回复能力护航的订单数')}
        {...restProps}
        className={css`
          .ant-drawer-body {
            padding: 16px !important;
            margin: 0 !important;
          }
        `}
      >
        <Formik<LightWeightOrderFormikState> initialValues={initialValues} onSubmit={() => {}}>
          {() => {
            return <OrderLightWeightAiService />;
          }}
        </Formik>
      </Drawer>
    );
  },
);

OrderLightWeightAiServiceModal.displayName = 'OrderLightWeightAiServiceModal';

export const OrderLightWeightAiService = React.memo(() => {
  const { t } = useTranslate();

  return (
    <OrderContainer>
      <WindupChildren>
        <div className="p-[16px]">
          <div className="leading-[22px]">{t('请选择您希望订购的订单数')}：</div>
        </div>
        <FormItems />
      </WindupChildren>
    </OrderContainer>
  );
});

OrderLightWeightAiService.displayName = 'OrderLightWeightAiService';

const FormItems = () => {
  const { t } = useTranslate();
  const isEnterprisePkg = useIsEnterprisePkg();
  const { field: list, helpers } =
    useSmartField<LightWeightOrderFormikState['unitOrderPackageList']>('unitOrderPackageList');
  const { helpers: helpers2 } = useSmartField<string>('currency');
  const { values, initialValues, setFieldValue } = useFormikContext<LightWeightOrderFormikState>();
  const { helpers: customNumHelpers } = useSmartField<number>('customNum');

  const {
    currenciesOptions,
    availablePayTypes,
    currency,
    setCurrency,
    payType,
    setPayType,
    bank,
    setBank,
  } = usePayPackage();

  React.useEffect(() => {
    helpers2.setValue(currency);
  }, [currency, helpers2]);
  return (
    <Container>
      <>
        <div className="mb-[20px]">
          <h4>{t('订购额度')}</h4>
          <div className="flex gap-[16px]">
            {list.value.map((each) => {
              return (
                <CheckContainer
                  key={each.orderNumber}
                  checked={each.isSelected}
                  onChange={(checked) => {
                    if (checked) {
                      helpers.setValue([
                        ...list.value.map((item) => {
                          return {
                            ...item,
                            isSelected: item.orderNumber === each.orderNumber,
                          };
                        }),
                      ]);
                      setFieldValue('isCustom', false);
                    }
                  }}
                >
                  <div className="leading-[20px] text-[var(--fett-text-primary)]">
                    {t('{x}单', {
                      x: formatNumber(each.orderNumber, '0,0[.]00'),
                    })}
                  </div>
                </CheckContainer>
              );
            })}
            <CheckContainer
              key="custom"
              checked={values.isCustom === true}
              onChange={(checked) => {
                if (checked) {
                  helpers.setValue([
                    ...list.value.map((item) => {
                      return {
                        ...item,
                        isSelected: false,
                      };
                    }),
                  ]);
                  setFieldValue('isCustom', true);
                  customNumHelpers.setValue(initialValues.customNum!);
                }
              }}
            >
              <div className="leading-[20px] text-[var(--fett-text-primary)]">{t('custom')}</div>
            </CheckContainer>
          </div>
          {!values.isCustom && <BonusTag />}
        </div>
        {values.isCustom && (
          <div className="mb-[20px]">
            <h4>{t('自定义订购额度')}</h4>
            <InputNumber
              step={100}
              max={1000000}
              canInput
              min={values.minNum}
              value={values.customNum}
              onChange={(value) => {
                if (value) {
                  customNumHelpers.setValue(value as number);
                }
              }}
              stepInfoTemplate={t('订购额度仅支持x的倍数', { x: 100 })}
              minInfoTemplate={t('订购额度最低x', { x: values.minNum })}
              maxInfoTemplate={t('订购额度最高x', { x: 1000000 })}
            />
            <BonusTag />
          </div>
        )}
        <div className="mb-[20px]">
          <CurrencyCheckbox
            currencies={currenciesOptions}
            value={currency}
            onChange={setCurrency}
          />
        </div>
        {isEnterprisePkg ? null : (
          <div>
            <PayTypeCheckbox
              payTypes={availablePayTypes}
              value={payType}
              onChange={setPayType}
              currency={currency}
              bank={bank}
              onBankChange={setBank}
            />
          </div>
        )}
        {currency === 'CNY' && (
          <div className="mt-[-12px]">
            <span className="mr-[6px]">
              {t('若需要开具公司抬头增值税发票，请使用公司支付宝进行支付')}
            </span>
            <CompanyAliHelp />
            <Help />
          </div>
        )}

        <LightWeightOrderResult payType={payType} bank={bank} />
      </>
    </Container>
  );
};

const BonusTag = () => {
  const { t } = useTranslate();
  const { values } = useFormikContext<LightWeightOrderFormikState>();
  const getNum = useMemoizedFn((values: Partial<LightWeightOrderFormikState>): number => {
    if (values.promotionType === 'FIRST_PURCHASE') {
      const inn = values.isCustom
        ? values.customNum!
        : find(values.unitOrderPackageList, { isSelected: true })?.orderNumber!;
      return !!values.bonusPercentage
        ? Math.ceil((inn * (1 + values.bonusPercentage)) / 100) * 100
        : inn;
    } else {
      return 0;
    }
  });
  const [num, setNum] = useState(() => {
    return getNum(values);
  });
  const { totalOrderNum, ...rest } = values;
  useEffect(() => {
    if (totalOrderNum) {
      setNum(totalOrderNum);
    }
  }, [totalOrderNum]);
  useEffect(() => {
    setNum(getNum(rest));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rest]);

  if (values.promotionType !== 'FIRST_PURCHASE') return null;
  if (!values.bonusPercentage) return null;

  return (
    <div className="inline-block mt-[10px] h-[26px] px-[5px] leading-[26px] bg-[#ffd6d6] text-[var(--fett-surface-error-text)] text-[12px] font-bold rounded-[2px]">
      {t('首次订购额外赠送{p}%流量（共计{x}）', {
        p: Number((get(values, 'bonusPercentage', 0) * 100).toFixed(0)),
        x: formatNumber(num, '0,0[.]00'),
      })}
    </div>
  );
};

const Container = styled.div`
  padding: 0px 16px 16px 16px;
  h4 {
    font-weight: bold;
  }
  label {
    border-radius: 4px;
  }
`;

const OrderContainer = (props: React.PropsWithChildren<{}>) => {
  const { children } = props;
  return (
    <div className="flex">
      <div className="w-[56px] h-[56px] mr-[12px]">
        <img className="h-full w-full" src={AvatarPng} alt="le" />
      </div>
      <div className="flex-grow bg-[var(--fett-erp-card-bg)] rounded-[4px]">{children}</div>
    </div>
  );
};

export const useGetLightWeightPkgInfo = () => {
  const { data, isLoading, mutateAsync } = useMutation({
    mutationKey: ['getLightWeightPkgInfo'],
    mutationFn: () => {
      return getLightWeightPkgInfo();
    },
  });

  return {
    data,
    loading: isLoading,
    mutateAsync,
  };
};
